import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { PointerEvent as ReactPointerEvent } from 'react';
import { useVault } from '../session/useVault';
import { useTheme } from '../design/useTheme';
import { arcTint } from '../design/tokens';
import { flat, listOf, parseEntity, transcriptTopics } from '../entity/entity';
import type { Topic } from '../entity/entity';
import type { VaultEntity } from '../entity/entity';
import { usePlan } from '../plan/usePlan';
import { isArcIn } from '../vault/kinds';
import type { Plan } from '../plan/loadPlan';
import { tagTint } from '../plan/registries';
import { StatusGlyph } from '../board/StatusGlyph';
import type { Status } from '../plan/arc';
import { linksRebased, placementsRebased, readLinks, readPlane, refKey, withLinks, withPlane } from '../plane/planeKey';
import { Curves } from '../plane/Curves';
import type { DrawnCurve } from '../plane/Curves';
import type { PlaneLink, PlanePlacement } from '../plane/planeKey';
import { useCamera } from '../plane/useCamera';
import { PlaneSurface } from '../plane/PlaneSurface';
import { arcHref } from './ArcRoute';
import { Loading } from '../shell/Loading';

const CARD_WIDTH = 300;
/** Below this, a card drops to its title alone. The ladder that keeps the
 *  board quiet is the plane's level of detail — not a second rule. */
const LOD_ZOOM = 0.6;
/** Approximate heights, for marquee hit-testing only. Measuring every node
 *  per pointermove would be exact and slower than the gesture needs. */
const CARD_H = 92;
const TOPIC_H = 66;
const HEADER_H = 34;

/**
 * The plane over an arc — a MODE of the arc, not a destination.
 *
 * Its members laid out in space, positions persisted to the `plane:` key so
 * an arrangement is real synced data rather than a browser's private memory.
 * The commit is the DROP, not the drag: one write per gesture, because a drag
 * that emits fifty writes is a real problem on this vault.
 */
export function PlaneRoute() {
  const { arcId } = useParams();
  const { state, write } = useVault();
  const { theme } = useTheme();
  const { plan: planState } = usePlan();
  const plan = planState.status === 'ready' ? planState.plan : null;
  const [placements, setPlacements] = useState<PlanePlacement[]>([]);
  const [dragging, setDragging] = useState<string | null>(null);
  /** Topics per source file, read on demand — the plane holds an arc's refs,
   *  not its members' bodies. */
  const [topicsByRef, setTopicsByRef] = useState<Map<string, Topic[]>>(new Map());
  /** Members fan out on the frame after they appear, so the motion starts
   *  from the card they came out of. */
  const [fanning, setFanning] = useState(false);
  /** Locked by default: you cannot mess up an arrangement you have not made
   *  yet. View state, not data. */
  const [links, setLinks] = useState<PlaneLink[]>([]);
  /** Awaiting a target click, after `c` or the menu. */
  const [linking, setLinking] = useState<string | null>(null);
  /** A selected line, by its index. Lines and cards are never selected at
   *  once — ⌫ has to mean one thing. */
  const [selectedEdge, setSelectedEdge] = useState<number | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [marquee, setMarquee] = useState<{ x0: number; y0: number; x1: number; y1: number } | null>(null);
  const [menu, setMenu] = useState<{ x: number; y: number; key: string } | null>(null);
  const surface = useRef<HTMLDivElement>(null);
  const cam = useCamera();

  const name = decodeURIComponent(arcId ?? '');

  // The plan is shared; what is local is this arc's layout, which is read out
  // of it whenever the arc this page is on changes underneath.
  useEffect(() => {
    if (planState.status !== 'ready') return;
    const arc = planState.plan.arcs.find((a) => a.name === name);
    if (!arc) return;
    setPlacements(readPlane(arc));
    setLinks(readLinks(arc));
  }, [planState, name]);

  const arc = plan?.arcs.find((a) => a.name === name);

  const menuName = (key: string): string => {
    const { ref, anchor } = keyOf(key);
    if (anchor !== undefined) {
      return topicsByRef.get(ref)?.find((tp) => tp.anchor === anchor)?.title ?? anchor;
    }
    return plan?.arcs.find((a) => a.path === ref)?.title ?? bare(ref);
  };

  /** Every member, whether or not it has been placed. An unplaced one is
   *  seeded from its order in the arc — the layout is a starting scatter,
   *  never an organising act. */
  const cards = useMemo(() => {
    if (!arc) return [];
    const refs = [...listOf(arc.frontMatter['arcs']), ...listOf(arc.frontMatter['sources'])];
    const placed = new Map(placements.map((p) => [p.ref, p]));
    return refs.map((raw, i) => {
      const [ref = '', ...anchors] = raw.split(' ^');
      const at = placed.get(ref.trim());
      return {
        ref: ref.trim(),
        anchors: anchors.map((a) => a.trim()),
        x: at?.x ?? 40 + (i % 3) * (CARD_WIDTH + 28),
        y: at?.y ?? 40 + Math.floor(i / 3) * 150,
        arranged: at !== undefined,
      };
    });
  }, [arc, placements]);

  /** Everything selectable, in one list: parents (or their headers) and any
   *  exploded topics. A key is `ref` or `ref#anchor`. */
  const hitboxes = useMemo(() => {
    const boxes: { key: string; x: number; y: number; w: number; h: number }[] = [];
    for (const c of cards) {
      const exploded = placements.some((p) => p.ref === c.ref && p.anchors.length > 0);
      boxes.push({ key: c.ref, x: c.x, y: c.y, w: CARD_WIDTH, h: exploded ? HEADER_H : CARD_H });
    }
    for (const p of placements) {
      if (p.anchors.length === 0) continue;
      boxes.push({ key: `${p.ref}#${p.anchors[0]}`, x: p.x, y: p.y, w: CARD_WIDTH, h: TOPIC_H });
    }
    return boxes;
  }, [cards, placements]);

  /** A link is drawn only while both its ends are placed: removing a card
   *  from the plane hides its lines without deleting them, and putting it
   *  back brings them back. */
  const curves = useMemo(() => {
    const placed = new Set(placements.map(refKey));
    const byKey = new Map(hitboxes.map((b) => [b.key, b]));
    return links.flatMap((l, index): DrawnCurve[] => {
      const from = placed.has(refKey(l.from)) ? byKey.get(refKey(l.from)) : undefined;
      const to = placed.has(refKey(l.to)) ? byKey.get(refKey(l.to)) : undefined;
      return from && to ? [{ key: String(index), from, to }] : [];
    });
  }, [links, placements, hitboxes]);

  /** Locked unless the placement says otherwise — the default is what you
   *  get before you have arranged anything. */
  const isUnlocked = useCallback(
    (ref: string) =>
      placements.find((p) => p.ref === ref && p.anchors.length === 0)?.unlocked === true,
    [placements],
  );

  const commit = useCallback(
    async (next: PlanePlacement[], nextLinks?: PlaneLink[]) => {
      if (!arc) return;
      const before = placements;
      const beforeLinks = links;
      setPlacements(next);
      if (nextLinks) setLinks(nextLinks);
      // Positions and structure are separate keys, so a drag rewrites only
      // what it changed.
      const change = (t: string) => {
        const fresh = parseEntity(arc.path, t);
        const placed = withPlane(t, placementsRebased(readPlane(fresh), before, next));
        return nextLinks ? withLinks(placed, linksRebased(readLinks(fresh), beforeLinks, nextLinks)) : placed;
      };
      write([{ path: arc.path, text: change(arc.raw), change }]);
    },
    [arc, placements, links, write],
  );

  const toggleLock = useCallback(
    (ref: string) => {
      void commit(
        placements.map((p) =>
          p.ref === ref && p.anchors.length === 0 ? { ...p, unlocked: !p.unlocked } : p,
        ),
      );
    },
    [placements, commit],
  );

  const connect = useCallback(
    (fromKey: string, toKey: string) => {
      if (fromKey === toKey) return;
      const from = keyOf(fromKey);
      const to = keyOf(toKey);
      const link: PlaneLink = {
        from: { ref: from.ref, anchors: from.anchor ? [from.anchor] : [] },
        to: { ref: to.ref, anchors: to.anchor ? [to.anchor] : [] },
      };
      // Already joined, either way round — a second line would draw over the
      // first and delete would only remove one of them.
      const exists = links.some(
        (l) =>
          (refKey(l.from) === fromKey && refKey(l.to) === toKey) ||
          (refKey(l.from) === toKey && refKey(l.to) === fromKey),
      );
      if (exists) return;
      void commit(placements, [...links, link]);
    },
    [links, placements, commit],
  );

  /** A ref is exploded when its topics carry positions of their own. No new
   *  field: the arrangement itself records the state. */
  const explodedRefs = useMemo(
    () => new Set(placements.filter((p) => p.anchors.length > 0).map((p) => p.ref)),
    [placements],
  );

  const topicsFor = useCallback(
    async (ref: string): Promise<Topic[]> => {
      const known = topicsByRef.get(ref);
      if (known) return known;
      if (state.status !== 'ready') return [];
      const text = await state.vault.readFile(ref);
      const topics = text ? transcriptTopics(parseEntity(ref, text)) : [];
      setTopicsByRef((m) => new Map(m).set(ref, topics));
      return topics;
    },
    [state, topicsByRef],
  );

  // Titles for anything already exploded when the page opens.
  useEffect(() => {
    for (const ref of explodedRefs) if (!topicsByRef.has(ref)) void topicsFor(ref);
  }, [explodedRefs, topicsByRef, topicsFor]);

  /**
   * Descending is a hierarchy move: a card becomes its direct inner items.
   * The parent does not vanish — it becomes the cluster header, so the
   * recording's identity is never lost. The cluster IS the recording.
   */
  const explode = useCallback(
    async (ref: string) => {
      const parent = cards.find((c) => c.ref === ref);
      if (!parent || explodedRefs.has(ref)) return;
      const topics = await topicsFor(ref);
      if (topics.length === 0) return;

      // Seeded from their order inside the parent — a scatter, never an
      // organising act.
      const seeded = topics.map((topic) => ({
        ref,
        anchors: [topic.anchor],
        x: parent.x,
        y: parent.y,
      }));
      setPlacements((current) => [...current, ...seeded]);
      setFanning(true);

      requestAnimationFrame(() => {
        const fanned = topics.map((topic, i) => ({
          ref,
          anchors: [topic.anchor],
          x: parent.x + 24 + (i % 2) * (CARD_WIDTH + 20),
          y: parent.y + 58 + Math.floor(i / 2) * 104,
        }));
        setPlacements((current) => [
          ...current.filter((p) => !(p.ref === ref && p.anchors.length > 0)),
          ...fanned,
        ]);
        setTimeout(() => {
          setFanning(false);
          void commit([
            ...placements.filter((p) => !(p.ref === ref && p.anchors.length > 0)),
            ...fanned,
          ]);
        }, 260 + topics.length * 30);
      });
    },
    [cards, explodedRefs, placements, topicsFor, commit],
  );

  /** Exactly reversed: the members go back inside, the header becomes a card
   *  again, and the parent keeps where it was. */
  const collapse = useCallback(
    (ref: string) => {
      const next = placements.filter((p) => !(p.ref === ref && p.anchors.length > 0));
      void commit(next);
    },
    [placements, commit],
  );

  const onCardDown = (key: string) => (event: ReactPointerEvent) => {
    if (event.button !== 0) return;
    event.stopPropagation();
    setMenu(null);

    // While a link is in flight, the next card you press is its target —
    // pressing anything else is how you aim, not how you drag.
    if (linking) {
      connect(linking, key);
      setLinking(null);
      return;
    }

    // Shift extends the selection rather than starting a drag — the two
    // gestures share a button, so they cannot share a meaning.
    if (event.shiftKey) {
      setSelected((s) => {
        const next = new Set(s);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next;
      });
      return;
    }
    if (!selected.has(key)) setSelected(new Set());
    setDragging(key);
  };

  const onSurfaceDown = (event: ReactPointerEvent) => {
    setMenu(null);
    if (event.button !== 0 || !surface.current) return;
    // A drag on empty canvas selects. A pointer can say which it means, so
    // there is no mode to switch.
    const at = cam.toCanvas(event.clientX, event.clientY, surface.current.getBoundingClientRect());
    if (!event.shiftKey) setSelected(new Set());
    setMarquee({ x0: at.x, y0: at.y, x1: at.x, y1: at.y });
  };

  const onMove = (event: ReactPointerEvent) => {
    if (!surface.current) return;
    const rect = surface.current.getBoundingClientRect();
    const at = cam.toCanvas(event.clientX, event.clientY, rect);

    if (marquee) {
      setMarquee({ ...marquee, x1: at.x, y1: at.y });
      return;
    }
    if (!dragging) return;

    // A multi-selection moves together, by the same delta.
    if (selected.size > 1 && selected.has(dragging)) {
      const anchor = keyOf(dragging);
      const target = placementFor(placements, anchor);
      if (target) {
        const dx = at.x - CARD_WIDTH / 2 - target.x;
        const dy = at.y - 24 - target.y;
        setPlacements((current) =>
          current.map((p) =>
            selected.has(p.anchors.length ? `${p.ref}#${p.anchors[0]}` : p.ref)
              ? { ...p, x: p.x + dx, y: p.y + dy }
              : p,
          ),
        );
      }
      return;
    }
    setPlacements((current) => {
      const [ref, anchor] = dragging.split('#');
      const target = current.find(
        (p) => p.ref === ref && (anchor ? p.anchors[0] === anchor : p.anchors.length === 0),
      );
      const nx = at.x - CARD_WIDTH / 2;
      const ny = at.y - 24;

      // A locked cluster moves as one unit — its header and every member
      // shift by the same delta, so an arrangement inside it survives.
      if (anchor && !isUnlocked(ref!) && target) {
        const dx = nx - target.x;
        const dy = ny - target.y;
        return current.map((p) => (p.ref === ref ? { ...p, x: p.x + dx, y: p.y + dy } : p));
      }
      if (!anchor && explodedRefs.has(ref!) && !isUnlocked(ref!)) {
        const header = current.find((p) => p.ref === ref && p.anchors.length === 0);
        const dx = nx - (header?.x ?? nx);
        const dy = ny - (header?.y ?? ny);
        const moved = current.map((p) => (p.ref === ref ? { ...p, x: p.x + dx, y: p.y + dy } : p));
        return header ? moved : [...moved, { ref: ref!, anchors: [], x: nx, y: ny }];
      }

      const rest = current.filter(
        (p) => !(p.ref === ref && (anchor ? p.anchors[0] === anchor : p.anchors.length === 0)),
      );
      return [...rest, { ref: ref!, anchors: anchor ? [anchor] : [], x: nx, y: ny }];
    });
  };

  const onUp = () => {
    if (marquee) {
      const lo = { x: Math.min(marquee.x0, marquee.x1), y: Math.min(marquee.y0, marquee.y1) };
      const hi = { x: Math.max(marquee.x0, marquee.x1), y: Math.max(marquee.y0, marquee.y1) };
      const caught = hitboxes
        .filter((b) => b.x < hi.x && b.x + b.w > lo.x && b.y < hi.y && b.y + b.h > lo.y)
        .map((b) => b.key);
      setSelected((s) => new Set([...s, ...caught]));
      setMarquee(null);
      return;
    }
    if (dragging) {
      setDragging(null);
      void commit(placements);
    }
  };

  /** Clears positions. It never deletes the item — the thing stays in the
   *  arc, it simply stops having a place on this canvas. */
  const removeFromPlane = useCallback(
    (keys: Set<string>) => {
      const next = placements.filter(
        (p) => !keys.has(p.anchors.length ? `${p.ref}#${p.anchors[0]}` : p.ref),
      );
      setSelected(new Set());
      void commit(next);
    },
    [placements, commit],
  );

  // esc lets go, ⌫ clears positions, ⌘↓ / ⌘↑ descend and collapse. Never
  // while something is being typed into.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const el = event.target as HTMLElement | null;
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)) return;

      if (event.key === 'Escape') {
        setSelected(new Set());
        setMenu(null);
        setLinking(null);
        setSelectedEdge(null);
        return;
      }
      if ((event.key === 'Backspace' || event.key === 'Delete') && selectedEdge !== null) {
        event.preventDefault();
        void commit(
          placements,
          links.filter((_, i) => i !== selectedEdge),
        );
        setSelectedEdge(null);
        return;
      }
      if ((event.key === 'Backspace' || event.key === 'Delete') && selected.size > 0) {
        event.preventDefault();
        removeFromPlane(selected);
        return;
      }
      if (event.key === 'c' && selected.size === 1 && !event.metaKey && !event.ctrlKey) {
        setLinking([...selected][0]!);
        return;
      }
      if ((event.metaKey || event.ctrlKey) && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
        const only = selected.size === 1 ? [...selected][0] : undefined;
        if (!only) return;
        event.preventDefault();
        const { ref } = keyOf(only);
        if (event.key === 'ArrowDown') void explode(ref);
        else collapse(ref);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selected, removeFromPlane, explode, collapse, selectedEdge, links, placements, commit]);


  if (state.status === 'disconnected') {
    return (
      <section className="placeholder">
        <h1>Not connected</h1>
        <p>
          <Link to="/connect">Connect this browser</Link> to read your vault.
        </p>
      </section>
    );
  }

  if (!arc) return <Loading title="Opening…" />;

  return (
    <div className="plane-wrap">
      {/* You never lose what you are inside. */}
      <div className="plane-strip">
        <StatusGlyph status={flat(arc.frontMatter['status']) as Status} size={12} />
        <span className="plane-strip-name">{arc.title}</span>
        <span className="plane-strip-when">{flat(arc.frontMatter['done-when'])}</span>
        <span className="t-metaMono plane-strip-count">{cards.length} members</span>
        <Link className="mode-switch t-chipLabel" to={arcHref(arc.name)}>
          page
        </Link>
      </div>

      <PlaneSurface
        cam={cam}
        surfaceRef={surface}
        className={`${dragging ? 'dragging' : ''}${linking ? ' linking' : ''}`}
        onPointerDown={onSurfaceDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        overlay={
          <>
            {marquee && (
              <div
                className="marquee"
                style={{
                  left: Math.min(marquee.x0, marquee.x1) * cam.camera.zoom + cam.camera.x,
                  top: Math.min(marquee.y0, marquee.y1) * cam.camera.zoom + cam.camera.y,
                  width: Math.abs(marquee.x1 - marquee.x0) * cam.camera.zoom,
                  height: Math.abs(marquee.y1 - marquee.y0) * cam.camera.zoom,
                }}
              />
            )}

            {menu && (
              <PlaneMenu
                at={menu}
                name={menuName(menu.key)}
                exploded={explodedRefs.has(keyOf(menu.key).ref)}
                isTopic={keyOf(menu.key).anchor !== undefined}
                locked={!isUnlocked(keyOf(menu.key).ref)}
                count={selected.size}
                onOpen={() => {
                  const { ref } = keyOf(menu.key);
                  window.location.assign(`/file/${encodeURIComponent(ref)}`);
                }}
                onExplode={() => void explode(keyOf(menu.key).ref)}
                onCollapse={() => collapse(keyOf(menu.key).ref)}
                onToggleLock={() => toggleLock(keyOf(menu.key).ref)}
                onConnect={() => setLinking(menu.key)}
                onRemove={() =>
                  removeFromPlane(selected.size > 1 ? selected : new Set([menu.key]))
                }
                onClose={() => setMenu(null)}
              />
            )}

            <div className="plane-hud t-metaMono">
              <span>
                {linking ? 'click a card to connect · esc cancels' : 'arranged · saved to the vault'}
              </span>
            </div>

            <div className="camera-controls">
              <button type="button" onClick={cam.reset}>
                fit
              </button>
              <button type="button" onClick={() => cam.zoomBy(1 / cam.camera.zoom)}>
                100%
              </button>
              <button type="button" onClick={() => cam.zoomBy(0.85)}>
                −
              </button>
              <button type="button" onClick={() => cam.zoomBy(1.18)}>
                +
              </button>
            </div>
          </>
        }
      >
        <Curves
          curves={curves}
          selected={selectedEdge === null ? null : String(selectedEdge)}
          onSelect={(key) => {
            setSelectedEdge(Number(key));
            setSelected(new Set());
          }}
        />

        {cards
          .filter((c) => explodedRefs.has(c.ref))
          .map((c) => (
            <Cluster
              key={`box-${c.ref}`}
              members={placements.filter((p) => p.ref === c.ref && p.anchors.length > 0)}
              header={c}
            />
          ))}

        {cards.map((card) =>
          explodedRefs.has(card.ref) ? (
            <ClusterHeader
              key={card.ref}
              card={card}
              title={plan?.arcs.find((a) => a.path === card.ref)?.title ?? bare(card.ref)}
              inside={placements.filter((p) => p.ref === card.ref && p.anchors.length > 0).length}
              locked={!isUnlocked(card.ref)}
              onToggleLock={() => toggleLock(card.ref)}
              onCollapse={() => collapse(card.ref)}
              onPointerDown={onCardDown(card.ref)}
            />
          ) : (
            <PlaneCard
              key={card.ref}
              card={card}
              plan={plan}
              theme={theme}
              lod={cam.camera.zoom < LOD_ZOOM}
              dragging={dragging === card.ref}
              onPointerDown={onCardDown(card.ref)}
              onExplode={() => void explode(card.ref)}
              selected={selected.has(card.ref)}
              onMenu={(e) => {
                if (!selected.has(card.ref)) setSelected(new Set([card.ref]));
                setMenu({ x: e.clientX, y: e.clientY, key: card.ref });
              }}
            />
          ),
        )}

        {placements
          .filter((p) => p.anchors.length > 0)
          .map((p, i) => {
            const key = `${p.ref}#${p.anchors[0]}`;
            const topic = topicsByRef.get(p.ref)?.find((tp) => tp.anchor === p.anchors[0]);
            return (
              <article
                key={key}
                className={
                  `plane-card topic-card${fanning ? ' fanning' : ''}` +
                  `${dragging === key ? ' dragging' : ''}${selected.has(key) ? ' selected' : ''}`
                }
                style={{
                  left: p.x,
                  top: p.y,
                  width: CARD_WIDTH,
                  transitionDelay: fanning ? `${i * 30}ms` : undefined,
                }}
                onPointerDown={onCardDown(key)}
                onContextMenu={(e) => {
                  e.preventDefault();
                  if (!selected.has(key)) setSelected(new Set([key]));
                  setMenu({ x: e.clientX, y: e.clientY, key });
                }}
              >
                <div className="t-metaMono plane-card-eyebrow">topic</div>
                <h3 className={`plane-card-title${topic?.unnamed ? ' unnamed' : ''}`}>
                  {topic?.title ?? p.anchors[0]}
                </h3>
              </article>
            );
          })}
      </PlaneSurface>
    </div>
  );
}

function PlaneCard({
  card,
  plan,
  theme,
  lod,
  dragging,
  onPointerDown,
  onExplode,
  selected,
  onMenu,
}: {
  card: { ref: string; anchors: string[]; x: number; y: number; arranged: boolean };
  plan: Plan | null;
  theme: ReturnType<typeof useTheme>['theme'];
  lod: boolean;
  dragging: boolean;
  onPointerDown: (event: ReactPointerEvent) => void;
  onExplode: () => void;
  selected: boolean;
  onMenu: (event: React.MouseEvent) => void;
}) {
  const name = bare(card.ref);
  const member: VaultEntity | undefined = plan?.arcs.find((a) => a.path === card.ref);
  const tag = member ? listOf(member.frontMatter['tags'])[0] : undefined;
  const hue = tag && plan ? tagTint(plan.registries, tag) : null;
  const tint = hue ? arcTint(theme, hue) : null;

  return (
    <article
      className={
        `plane-card${dragging ? ' dragging' : ''}${card.arranged ? '' : ' unarranged'}` +
        `${selected ? ' selected' : ''}`
      }
      style={{
        left: card.x,
        top: card.y,
        width: CARD_WIDTH,
        ...(tint ? { background: tint.fill, borderColor: tint.border } : {}),
      }}
      onPointerDown={onPointerDown}
      onDoubleClick={onExplode}
      onContextMenu={(e) => {
        e.preventDefault();
        onMenu(e);
      }}
      title="Double-click to explode into topics"
    >
      <div className="t-metaMono plane-card-eyebrow">
        {isArcIn(card.ref, plan?.arcs) ? 'arc' : 'recording'}
        {card.anchors.length > 0 && <span> · {card.anchors.length} topics</span>}
      </div>
      <h3 className="plane-card-title">{member?.title ?? name}</h3>
      {!lod && member && (
        <p className="plane-card-face t-faceProse">{flat(member.frontMatter['done-when'])}</p>
      )}
    </article>
  );
}

/**
 * The boundary, drawn behind its members. The parent did not vanish — it is
 * the header — so the recording's identity survives the descent.
 */
function Cluster({
  members,
  header,
}: {
  members: PlanePlacement[];
  header: { x: number; y: number };
}) {
  if (members.length === 0) return null;
  const pad = 14;
  const left = Math.min(header.x, ...members.map((m) => m.x)) - pad;
  const top = Math.min(header.y, ...members.map((m) => m.y)) - pad;
  const right = Math.max(header.x + CARD_WIDTH, ...members.map((m) => m.x + CARD_WIDTH)) + pad;
  const bottom = Math.max(header.y + 40, ...members.map((m) => m.y + 78)) + pad;

  return (
    <div
      className="cluster"
      style={{ left, top, width: right - left, height: bottom - top }}
      aria-hidden="true"
    />
  );
}

function ClusterHeader({
  card,
  title,
  inside,
  locked,
  onToggleLock,
  onCollapse,
  onPointerDown,
}: {
  card: { ref: string; x: number; y: number };
  title: string;
  inside: number;
  locked: boolean;
  onToggleLock: () => void;
  onCollapse: () => void;
  onPointerDown: (event: ReactPointerEvent) => void;
}) {
  return (
    <div
      className="cluster-head"
      style={{ left: card.x, top: card.y, width: CARD_WIDTH }}
      onPointerDown={onPointerDown}
      onDoubleClick={onCollapse}
    >
      <span className="cluster-name">{title}</span>
      <span className="t-metaMono cluster-count">{inside} inside</span>
      {/* A visible control. Long-press is a phone gesture and does not
          belong here. */}
      <button
        type="button"
        className={`cluster-btn${locked ? ' on' : ''}`}
        title={locked ? 'Locked — members move together' : 'Unlocked — members move alone'}
        onPointerDown={(e) => e.stopPropagation()}
        onClick={onToggleLock}
      >
        {locked ? '⚿' : '⚯'}
      </button>
      <button
        type="button"
        className="cluster-btn"
        title="Collapse"
        onPointerDown={(e) => e.stopPropagation()}
        onClick={onCollapse}
      >
        ⌄
      </button>
    </div>
  );
}

function bare(ref: string): string {
  return ref.replace(/^.*\//, '').replace(/\.md$/, '');
}

/**
 * The pointer's menu.
 *
 * Only what this surface can actually do. `Group these` and `Connect to…`
 * are in the spec and are omitted rather than shown disabled: a menu item
 * that never works teaches the wrong thing about the product, and both need
 * storage that does not exist yet.
 */
function PlaneMenu({
  at,
  name,
  exploded,
  isTopic,
  locked,
  count,
  onOpen,
  onExplode,
  onCollapse,
  onToggleLock,
  onConnect,
  onRemove,
  onClose,
}: {
  at: { x: number; y: number };
  name: string;
  exploded: boolean;
  isTopic: boolean;
  locked: boolean;
  count: number;
  onOpen: () => void;
  onExplode: () => void;
  onCollapse: () => void;
  onToggleLock: () => void;
  onConnect: () => void;
  onRemove: () => void;
  onClose: () => void;
}) {
  useEffect(() => {
    const away = () => onClose();
    const id = setTimeout(() => document.addEventListener('pointerdown', away));
    return () => {
      clearTimeout(id);
      document.removeEventListener('pointerdown', away);
    };
  }, [onClose]);

  const run = (fn: () => void) => () => {
    fn();
    onClose();
  };

  return (
    <div className="plane-menu" style={{ left: at.x, top: at.y }} role="menu">
      <button type="button" onClick={run(onOpen)}>
        Open <kbd className="kbd">↵</kbd>
      </button>
      {!isTopic && !exploded && (
        <button type="button" onClick={run(onExplode)}>
          Explode into topics <kbd className="kbd">⌘↓</kbd>
        </button>
      )}
      {!isTopic && exploded && (
        <>
          <button type="button" onClick={run(onCollapse)}>
            Collapse <kbd className="kbd">⌘↑</kbd>
          </button>
          <button type="button" onClick={run(onToggleLock)}>
            {locked ? 'Unlock cluster' : 'Lock cluster'}
          </button>
        </>
      )}
      <button type="button" onClick={run(onConnect)}>
        Connect to… <kbd className="kbd">c</kbd>
      </button>
      <span className="plane-menu-sep" />
      <button type="button" onClick={run(() => void navigator.clipboard?.writeText(name))}>
        Copy name
      </button>
      <button type="button" onClick={run(onRemove)}>
        {count > 1 ? `Remove ${count} from the canvas` : 'Remove from the canvas'}
        <kbd className="kbd">⌫</kbd>
      </button>
    </div>
  );
}

/** A card key is `ref` for a file and `ref#anchor` for one of its topics. */
function keyOf(key: string): { ref: string; anchor: string | undefined } {
  const [ref = '', anchor] = key.split('#');
  return { ref, anchor };
}

function placementFor(
  placements: PlanePlacement[],
  { ref, anchor }: { ref: string; anchor: string | undefined },
): PlanePlacement | undefined {
  return placements.find(
    (p) => p.ref === ref && (anchor ? p.anchors[0] === anchor : p.anchors.length === 0),
  );
}
